import { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import apiClient from "../api/client";
import { jwtDecode } from "jwt-decode";
import AuthContext from "../auth/context";
import authStorage from "../auth/storage";
import { useNavigate } from "react-router-dom";
export default function LoginPage() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    recoveryEmail: "",
    otp: "",
    newPassword: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const result = await apiClient.post("/auth", {
      ...formData,
      username: formData.email,
    });

    // Simulate error for demo (remove in production)
    if (!result.ok) {
      setError(result.data.error);
      setLoading(false);
      return;
    }

    const user = jwtDecode(result.data.token);
    authContext.setUser(user);
    authStorage.storeToken(result.data.token);
    navigate("/");

    setLoading(false);
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    await new Promise((resolve) => setTimeout(resolve, 1000));
    const result = await apiClient.post("/auth/forgot-password", {
      email: formData.recoveryEmail,
    });
    // Simulate error (remove in production)
    if (!result.ok) {
      setError(result.data.error);
      setLoading(false);
      return;
    }

    setLoading(false);
    setView("otp");
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await apiClient.post("/auth/verify-otp", {
        email: formData.recoveryEmail,
        otp: formData.otp,
        newPassword: formData.newPassword,
      });

      if (!result.ok) {
        setError(result.data.error);
        return;
      }

      // Success - return to login
      setView("login");
      setFormData({
        ...formData,
        otp: "",
        newPassword: "",
        recoveryEmail: "",
      });
    } catch (error) {
      setError("Errore durante la verifica. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(""); // Clear error when user types
  };

  const contentVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 200 : -200,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 200 : -200,
      opacity: 0,
      position: "absolute",
    }),
  };

  useEffect(() => {
    setError("");
  }, [view]);

  const renderView = () => {
    switch (view) {
      case "forgotPassword":
        return (
          <motion.div
            key="forgotPassword"
            initial="enter"
            animate="center"
            exit="exit"
            variants={contentVariants}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col items-center mb-8">
              <h2 className="text-center text-2xl font-bold text-[#8B8178]">
                Recupera Password
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Inserisci la tua email per recuperare la password
              </p>
            </div>
            {error && (
              <div className="alert mb-4 alert-error text-white text-sm">
                {error}
              </div>
            )}

            <motion.form className="space-y-6" onSubmit={handleRecoverySubmit}>
              <div>
                <label
                  htmlFor="recoveryEmail"
                  className="text-sm font-medium text-gray-700 block mb-2"
                >
                  Email
                </label>
                <input
                  id="recoveryEmail"
                  name="recoveryEmail"
                  type="email"
                  required
                  className="input input-bordered w-full transition-all duration-200 focus:ring-2 focus:ring-[#E31E24] focus:border-transparent"
                  value={formData.recoveryEmail}
                  onChange={handleChange}
                />
              </div>

              <div className="flex justify-between">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setView("login")}
                  className="text-sm font-medium text-[#E31E24]"
                >
                  Torna al login
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="btn bg-[#E31E24] hover:bg-[#b81118] text-white px-6"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="loading loading-spinner loading-sm"></div>
                  ) : (
                    "Invia"
                  )}
                </motion.button>
              </div>
            </motion.form>
          </motion.div>
        );

      case "otp":
        return (
          <motion.div
            key="otp"
            initial="enter"
            animate="center"
            exit="exit"
            variants={contentVariants}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col items-center mb-8">
              <h2 className="text-center text-2xl font-bold text-[#8B8178]">
                Verifica Codice
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Abbiamo inviato un codice alla tua email
              </p>
            </div>

            {error && (
              <div className="alert mb-4 alert-error text-white text-sm">
                {error}
              </div>
            )}

            <motion.form className="space-y-6" onSubmit={handleOTPSubmit}>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="otp"
                    className="text-sm font-medium text-gray-700 block mb-2"
                  >
                    Codice di Verifica
                  </label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    required
                    maxLength="6"
                    className="input input-bordered w-full text-center text-2xl tracking-widest"
                    value={formData.otp}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label
                    htmlFor="newPassword"
                    className="text-sm font-medium text-gray-700 block mb-2"
                  >
                    Nuova Password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    className="input input-bordered w-full"
                    value={formData.newPassword}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setView("forgotPassword")}
                  className="text-sm font-medium text-[#E31E24]"
                >
                  Indietro
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="btn bg-[#E31E24] hover:bg-[#b81118] text-white px-6"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="loading loading-spinner loading-sm"></div>
                  ) : (
                    "Conferma"
                  )}
                </motion.button>
              </div>
            </motion.form>
          </motion.div>
        );

      default:
        return (
          <motion.div
            key="login"
            initial="enter"
            animate="center"
            exit="exit"
            variants={contentVariants}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col items-center mb-8">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="h-14 w-auto mb-8"
                src={"./graziani.png"}
                alt="Graziani Logo"
              />
              <h2 className="text-center text-2xl font-bold text-[#8B8178]">
                Accedi al tuo account
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Inserisci le tue credenziali per continuare
              </p>
            </div>

            {error && (
              <div className="alert mb-4 alert-error text-white text-sm">
                {error}
              </div>
            )}

            <motion.form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-5">
                <motion.div>
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-700 block mb-2"
                  >
                    Indirizzo Email o username
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="text"
                    required
                    className="input input-bordered w-full transition-all duration-200 focus:ring-2 focus:ring-[#E31E24] focus:border-transparent"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </motion.div>

                <motion.div>
                  <label
                    htmlFor="password"
                    className="text-sm font-medium text-gray-700 block mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="input input-bordered w-full transition-all duration-200 focus:ring-2 focus:ring-[#E31E24] focus:border-transparent"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </motion.div>
              </div>

              <div className="flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => setView("forgotPassword")}
                  disabled={loading}
                  className="text-sm font-medium text-[#E31E24] hover:text-[#b81118] transition-colors duration-200"
                >
                  Password dimenticata?
                </motion.button>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="btn btn-block bg-[#E31E24] hover:bg-[#b81118] text-white
                  transition-all duration-200 rounded-xl h-12"
                disabled={loading}
              >
                {loading ? (
                  <div className="loading loading-spinner loading-sm"></div>
                ) : (
                  "Accedi"
                )}
              </motion.button>
            </motion.form>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full p-10 bg-white rounded-2xl shadow-xl relative overflow-hidden min-h-[500px]"
      >
        <AnimatePresence
          mode="wait"
          initial={false}
          custom={view === "login" ? -1 : 1}
        >
          {renderView()}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
